import {TruckStatus, useTruckStatus} from "../truck/truck.tsx";
import {SuspendedTile, Tile} from "../components/Tile.tsx";
import {DateArg, isAfter} from "date-fns";
import {useMemo} from "react";
import {Box, Sheet, SheetProps, Stack, useTheme} from "@mui/joy";
import {VenueCapsule} from "../venue/capsule.tsx";
import {Bundle, Transfer, useFullOpenTransfersForTruck} from "../rpc/webrpc-client.gen.ts";
import {useTranslation} from "react-i18next";
import {spawnTstzSpan, TSTZ_NEGATIVE_INFINITY} from "../tstzspan.ts";
import {TruckCapsule} from "../truck/capsule.tsx";
import {AgentPresenceCapsule, agentPresenceColor} from "../agent/capsule.tsx";
import {TriplegDirection} from "./TriplegTile.tsx";
import {HCBox} from "../components/HCBox.tsx";
import {DECO_DELAY_SCENES, useDecoDecay} from "./decay.tsx";
import {useTruckCrewPresences} from "../truck/tile.tsx";
import {faSteeringWheel} from "@fortawesome/pro-regular-svg-icons";
import {RouterJoyLink} from "../link.tsx";
import {TruckActor, TruckSceneTileContent, useTruckSceneColor} from "../truck/timeline.tsx";
import {pointillize} from "../timeline/timeline.tsx";
import {useLoadedBundles} from "./useLoadedBundles.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {cleanse, NADA} from "../util/arutil.tsx";
import {formatTileTimelineCoordinate} from "../timeline/formatTileTimelineCoordinate.ts";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
// function useBogusOpenTransfers() {
//     const rv = useQueries({
//         queries: NADA.map(() =>
//             watchBundleTransferByIdTqo({} as KingBasuraService, {} as WatchBundleTransferByIdArgs)),
//         combine: useMappingMergeUseQueryResultCombiner((rpc) => rpc.transfer)
//     });
//     return rv;
// }

interface DispatchTruckStatus {
    core: TruckStatus;
    openTransfers?: Transfer[];
    loadedBundles: Bundle[];
    direction: TriplegDirection;
    lastUpdated: DateArg<Date>;
}

function useDispatchTruckStatus(args ?: { truckId: string }): InfallibleUseResult<DispatchTruckStatus> {
    const usedCoreTqrv = useTruckStatus(args);
    const usedCore = usedCoreTqrv.data;
    const transferTqrv = useFullOpenTransfersForTruck(args);
    const openTransfers = transferTqrv.data ? cleanse(transferTqrv.data) : NADA;
    const loadedBundleTqrv = useLoadedBundles(args);
    const loadedBundles = loadedBundleTqrv.data;

    const direction = useMemo<TriplegDirection>(() => {
        // if our current tripleg is headed to somewhere that one of
        // our bundles wants to go, we are considered returning. otherwise
        // we are outbound. outbound trucks head right, returning trucks
        // face left.
        if (loadedBundles) {
            for (const bundle of loadedBundles) {
                if (bundle.destination?.id === usedCore?.lastTripleg?.destination) {
                    return "homebound";
                }
            }
        }
        return "outbound";
    }, [loadedBundles, usedCore?.lastTripleg?.destination]);

    let lastUpdated = useMemo<DateArg<Date>>(() => {
        let rv: DateArg<Date> = TSTZ_NEGATIVE_INFINITY;
        if (usedCore?.truck.validSpan) {
            const span = spawnTstzSpan(usedCore.truck.validSpan);
            const lower = span.lower;
            if (lower) {
                rv = lower;
            }
        }
        return rv;
    }, [usedCore?.truck.validSpan]);

    if (usedCore?.lastTripleg) {
        const tripleg = usedCore.lastTripleg;
        if (tripleg.admitted && isAfter(tripleg.admitted, lastUpdated)) {
            lastUpdated = tripleg.admitted;
        }
        if (tripleg.arrived && isAfter(tripleg.arrived, lastUpdated)) {
            lastUpdated = tripleg.arrived;
        }
        if (tripleg.departed && isAfter(tripleg.departed, lastUpdated)) {
            lastUpdated = tripleg.departed;
        }
    }

    // useWhatChanged([isOpenTransfersSuccess, lastUpdated, openTransferTqrvs, usedCore.data]);
    const rv = useMemo(() => {
        if (!usedCore || transferTqrv.isPending || !loadedBundles) {
            return pendingUseResult;
        }
        const rv = successUseResult({
            core: usedCore, openTransfers, loadedBundles, direction, lastUpdated
        });
        return rv;
    }, [direction, lastUpdated, loadedBundles, openTransfers, transferTqrv.isPending, usedCore]);
    // console.log(`dispatch truck status for truck id ${truckId} is ${JSON.stringify(rv)}`);
    return rv;
}

export interface DispatchTruckTileProps {
    dxt: TruckActor;
}

export function DispatchTruckTile({dxt}: DispatchTruckTileProps) {
    const truckId = dxt.truck.id;
    const {data: xstat} = useDispatchTruckStatus({truckId});

    const {t} = useTranslation();
    const theme = useTheme();

    const defaultColor = useMemo<SheetProps["color"]>(() => {
        // TODO: put truck on break when crew is on break?
        if (xstat?.core.currentCrew && xstat.core.currentCrew.length > 0) {
            let rv: SheetProps["color"] = "neutral";
            // if anybody's active, so are we. if not, pick somebody's status
            // at random to represent the truck
            for (const crew of xstat.core.currentCrew) {
                rv = agentPresenceColor(crew);
                if (rv === "primary") {
                    break;
                }
            }
            return rv;
        } else {
            return "neutral";
        }
    }, [xstat?.core.currentCrew]);

    const lastUpdated = dxt.lastScene ? pointillize(dxt.lastScene.when).at : TSTZ_NEGATIVE_INFINITY;
    const joyDecos = useDecoDecay({scenes: DECO_DELAY_SCENES, lastUpdated});
    const variant = joyDecos.variant;
    const {data: crewPresences} = useTruckCrewPresences({
        truckId: dxt.truck.id
    });
    const crewCapsules = (crewPresences ?? []).map(presence =>
        <AgentPresenceCapsule presence={presence} key={presence.agent.id} icon={{icon: faSteeringWheel}}/>);

    //const content: ReactNode[] = [];
    const sceneColor = useTruckSceneColor(dxt.lastScene);
    const color: SheetProps["color"] = sceneColor ?? defaultColor;

    if (!xstat) {
        return <SuspendedTile/>;
    }

    // if (!tripleg) {
    //     content.push(<FontAwesomeIcon icon={faTruckFront} size="2x" key="loneIcon"/>);
    // } else {
    //
    //     if (triplegContentData) {
    //         color = triplegContentData.color;
    //         absorb(content, triplegContentData.content);
    //     }
    //

    const truck = xstat.core.truck;
    return <Tile variant="outlined" color={color}
                 Subject={(props) => <RouterJoyLink
                     to="/truck/$id/cargo" params={{id: truck.id}}
                     color={color} underline="hover">
                     <TruckCapsule truck={truck} {...props}/>
                 </RouterJoyLink>}>
        {/* make sure destination shows even on the road */
            !!xstat.core.lastTripleg?.departed && !xstat.core.lastTripleg.arrived

                ? <Stack direction="row" sx={{fontSize: theme.fontSize.md, alignItems: "baseline"}}>
                    <VenueCapsule venue={xstat.core.lastTripleg.destination}/>
                    <Box sx={{flex: 0, ml: 0.6}}>{t("driver.destination")}</Box>
                </Stack>
                : null}

        {crewCapsules}

        {dxt.lastScene ? <TruckSceneTileContent color={color} scene={dxt.lastScene}/> : null}

        <Sheet color={color} variant={variant}>
            <HCBox>
                {t("dispatch.asof", {when: formatTileTimelineCoordinate({at: lastUpdated})})}
            </HCBox>
        </Sheet>
    </Tile>;
}





